import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface Banner {
  id: string;
  image_desktop: string | null;
  image_mobile: string | null;
  title: string | null;
  subtitle: string | null;
  cta_text: string | null;
  cta_link: string | null;
}

export const HeroBannerSlider = () => {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: "start" },
    [Autoplay({ delay: 5000, stopOnInteraction: false, stopOnMouseEnter: true })],
  );
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    const load = async () => {
      const nowIso = new Date().toISOString();
      const { data } = await supabase
        .from("banners")
        .select("*")
        .eq("is_active", true)
        .or(`start_date.is.null,start_date.lte.${nowIso}`)
        .or(`end_date.is.null,end_date.gte.${nowIso}`)
        .order("display_order", { ascending: true });
      setBanners((data as Banner[]) ?? []);
      setLoading(false);
    };
    load();
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  if (loading) {
    return (
      <section className="w-full bg-muted/40">
        <div
          className="w-full animate-pulse bg-gradient-to-r from-muted via-secondary to-muted aspect-[23/10] md:aspect-[16/5]"
        />
      </section>
    );
  }

  if (banners.length === 0) return null;

  return (
    <section className="relative w-full bg-background">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {banners.map((b) => {
            const Wrapper: any = b.cta_link ? "a" : "div";
            const wrapperProps = b.cta_link
              ? { href: b.cta_link, className: "block w-full" }
              : { className: "block w-full" };
            return (
              <div key={b.id} className="relative min-w-0 shrink-0 grow-0 basis-full">
                <Wrapper {...wrapperProps}>
                  <div
                    className="relative w-full overflow-hidden aspect-[23/10] md:aspect-[16/5]"
                  >
                    {/* Mobile image */}
                    {b.image_mobile && (
                      <img
                        src={b.image_mobile}
                        alt={b.title ?? "Banner"}
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover object-[70%_center] md:hidden"
                      />
                    )}
                    {/* Desktop image (fallback to mobile if missing) */}
                    {(b.image_desktop || b.image_mobile) && (
                      <img
                        src={b.image_desktop ?? b.image_mobile ?? ""}
                        alt={b.title ?? "Banner"}
                        loading="lazy"
                        className={cn(
                          "absolute inset-0 h-full w-full object-cover object-[65%_center]",
                          b.image_mobile ? "hidden md:block" : "block",
                        )}
                      />
                    )}

                    {(b.title || b.subtitle || b.cta_text) && (
                      <>
                        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />
                        <div className="absolute inset-0 flex items-center">
                          <div className="container">
                            <div className="max-w-xl text-white space-y-1.5 md:space-y-3 animate-fade-up">
                              {b.subtitle && (
                                <span className="inline-block rounded-full bg-white/15 backdrop-blur px-2.5 py-0.5 text-[10px] md:text-xs uppercase tracking-wider font-medium">
                                  {b.subtitle}
                                </span>
                              )}
                              {b.title && (
                                <h2
                                  className="font-display font-bold leading-[1.05] drop-shadow line-clamp-2"
                                  style={{ fontSize: "clamp(1rem, 3.2vw, 2.4rem)" }}
                                >
                                  {b.title}
                                </h2>
                              )}
                              {b.cta_text && (
                                <div className="pt-0.5 md:pt-1">
                                  <Button variant="hero" size="sm" className="h-9 md:h-11 text-xs md:text-sm">
                                    {b.cta_text} <ArrowRight className="ml-1 h-3.5 w-3.5 md:h-4 md:w-4" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </Wrapper>
              </div>
            );
          })}
        </div>
      </div>

      {banners.length > 1 && (
        <>
          <button
            aria-label="Previous slide"
            onClick={() => emblaApi?.scrollPrev()}
            className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 h-10 w-10 items-center justify-center rounded-full bg-background/70 backdrop-blur shadow-soft hover:bg-background transition"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            aria-label="Next slide"
            onClick={() => emblaApi?.scrollNext()}
            className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 h-10 w-10 items-center justify-center rounded-full bg-background/70 backdrop-blur shadow-soft hover:bg-background transition"
          >
            <ChevronRight className="h-5 w-5" />
          </button>

          <div className="absolute bottom-3 md:bottom-5 left-0 right-0 flex justify-center gap-2">
            {banners.map((_, i) => (
              <button
                key={i}
                aria-label={`Go to slide ${i + 1}`}
                onClick={() => emblaApi?.scrollTo(i)}
                className={cn(
                  "h-1.5 rounded-full transition-all",
                  i === selected ? "w-6 bg-white" : "w-1.5 bg-white/60 hover:bg-white/80",
                )}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
};
